import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import type { ConductorBundle, TechCalcDictionaries } from "../types";
import { fmtMeters } from "../utils";

/**
 * Parametry przęsła: lista pęczków przewodów (rodzina + przekrój + liczba żył)
 * oraz długość — zawsze widoczna z geometrii, z opcjonalnym ręcznym nadpisaniem,
 * które jest wyraźnie oznaczone (bursztynowa ramka + dopisek), żeby nikt nie
 * pomylił wartości ręcznej z pomierzoną.
 */
export function SpanParamsPanel({
  fromRef,
  toRef,
  lengthM,
  initialBundles,
  initialOverride,
  dictionaries,
  onCommit,
  onClose,
}: {
  fromRef: string;
  toRef: string;
  /** Live geometric length in meters. */
  lengthM: number;
  initialBundles: ConductorBundle[];
  initialOverride: number | null;
  dictionaries: TechCalcDictionaries | undefined;
  onCommit: (patch: { bundles?: ConductorBundle[]; length_override?: number | null }) => void;
  onClose: () => void;
}) {
  const [bundles, setBundles] = useState<ConductorBundle[]>(initialBundles);
  const [override, setOverride] = useState<string>(
    initialOverride != null ? String(initialOverride) : "",
  );

  const families = dictionaries?.families ?? [];
  const crossSections = dictionaries?.cross_sections ?? [];

  const commitBundles = (next: ConductorBundle[]) => {
    setBundles(next);
    onCommit({ bundles: next });
  };

  const updateBundle = (i: number, patch: Partial<ConductorBundle>) => {
    commitBundles(bundles.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  };

  const addBundle = () => {
    commitBundles([
      ...bundles,
      { family: families[0] ?? "", cross_section: crossSections[0] ?? 0, count: 1 },
    ]);
  };

  const removeBundle = (i: number) => commitBundles(bundles.filter((_, j) => j !== i));

  const commitOverride = (raw: string) => {
    setOverride(raw);
    if (raw.trim() === "") {
      onCommit({ length_override: null });
      return;
    }
    const n = Number(raw.replace(",", "."));
    if (Number.isFinite(n) && n > 0) onCommit({ length_override: n });
  };

  const hasOverride = override.trim() !== "";

  return (
    <div className="p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-secondary)]">
          Przęsło {fromRef} – {toRef}
        </span>
        <button
          type="button"
          className="text-[11px] text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)]"
          onClick={onClose}
        >
          Zamknij
        </button>
      </div>

      {/* ── pęczki przewodów ─────────────────────────────────────────────── */}
      <p className="section-label mb-1">Przewody</p>
      {bundles.length === 0 && (
        <p className="mb-1 text-[12px] text-[var(--color-text-muted)]">
          Brak przewodów — dodaj co najmniej jeden pęczek.
        </p>
      )}
      <div className="space-y-1.5">
        {bundles.map((b, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <select
              className="input-base w-28 py-1 text-[12px]"
              value={b.family}
              onChange={(e) => updateBundle(i, { family: e.target.value })}
              title="Rodzina przewodu"
            >
              {!families.includes(b.family) && b.family && (
                <option value={b.family}>{b.family}</option>
              )}
              {families.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
            <select
              className="input-base w-24 py-1 font-mono text-[12px]"
              value={String(b.cross_section)}
              onChange={(e) => updateBundle(i, { cross_section: Number(e.target.value) })}
              title="Przekrój [mm²]"
            >
              {!crossSections.includes(b.cross_section) && (
                <option value={String(b.cross_section)}>{b.cross_section}</option>
              )}
              {crossSections.map((cs) => (
                <option key={cs} value={String(cs)}>
                  {cs}
                </option>
              ))}
            </select>
            <input
              type="number"
              min={1}
              step={1}
              className="input-base w-16 py-1 font-mono text-[12px]"
              value={b.count}
              onChange={(e) => updateBundle(i, { count: Math.max(1, Number(e.target.value) || 1) })}
              title="Liczba żył"
            />
            <button
              type="button"
              className="p-1 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
              title="Usuń pęczek"
              onClick={() => removeBundle(i)}
            >
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={addBundle}
        className="mt-1.5 inline-flex items-center gap-1 border border-dashed border-[var(--color-border)] px-2 py-1 font-mono text-[11px] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)]"
      >
        <Plus size={12} /> Dodaj pęczek
      </button>
      <p className="mt-1 text-[10px] text-[var(--color-text-tertiary)]">
        rodzina · przekrój [mm²] · liczba żył
      </p>

      {/* ── długość ──────────────────────────────────────────────────────── */}
      <div className="mt-3 border-t border-[var(--color-border)] pt-2">
        <p className="section-label mb-1">Długość przęsła</p>
        <p className="font-mono text-[12px] text-[var(--color-text-secondary)]">
          Z geometrii: <b className="text-[var(--color-text-primary)]">{fmtMeters(lengthM)}</b>
        </p>
        <div className="mt-1 flex items-center gap-1.5">
          <input
            type="number"
            step="any"
            min={0}
            className={`input-base w-32 py-1 font-mono text-[12px] ${
              hasOverride ? "border-amber-500" : ""
            }`}
            placeholder="ręczna [m]"
            value={override}
            onChange={(e) => commitOverride(e.target.value)}
          />
          {hasOverride ? (
            <>
              <span className="font-mono text-[10px] font-bold uppercase text-amber-600">
                nadpisana ręcznie
              </span>
              <button
                type="button"
                className="text-[11px] text-[var(--color-text-tertiary)] hover:text-[var(--color-primary)]"
                onClick={() => commitOverride("")}
              >
                Wyczyść
              </button>
            </>
          ) : (
            <span className="text-[10px] text-[var(--color-text-tertiary)]">
              puste = długość z geometrii
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
